import React, { useState, useEffect } from 'react';
import { Mafs, Text, Polygon, Vector, Coordinates, Plot, Point, Theme } from 'mafs';
import * as math from 'mathjs';

export const FunctionMachine = ({ config, variables }) => {
    const [currentInputIndex, setCurrentInputIndex] = useState(0);
    const [animationState, setAnimationState] = useState('idle');
    const [processedResults, setProcessedResults] = useState([]);

    const rule = config.rule || "x + 2";
    const inputValues = config.inputValues || [variables.input ?? config.input ?? 5];
    const animation = config.animation || { enabled: false, speed: "normal" };
    const showInverse = config.showInverse || false;
    const inverseRule = config.inverseRule || null;
    const studentRule = config.studentRule || null;

    const currentInput = inputValues[currentInputIndex];

    // Helper: Format number for display
    const formatNum = (num) => {
        if (!Number.isFinite(num)) return num;
        return Math.abs(num - Math.round(num)) < 0.0001
            ? Math.round(num)
            : parseFloat(num.toFixed(3));
    };

    const calculateOutput = (input, ruleExpr) => {
        try {
            return math.evaluate(ruleExpr, { x: Number(input) });
        } catch (e) {
            return "Error";
        }
    };

    const calculateSteps = (input, ruleExpr) => {
        const steps = [];
        steps.push({ description: `x = ${input}`, value: input });

        try {
            // Parse the expression into an AST
            const node = math.parse(ruleExpr);

            // Helper: Evaluate a node with the current x value
            const evaluateNode = (node) => {
                return node.evaluate({ x: input });
            };

            // Helper: Convert node to readable string
            const nodeToString = (node) => {
                return node.toString().replace(/\*/g, '×').replace(/\//g, '÷');
            };

            // Helper: Check if node is simple (constant or variable)
            const isSimple = (node) => {
                return node.type === 'ConstantNode' ||
                       (node.type === 'SymbolNode' && node.name === 'x');
            };

            // Build steps by analyzing the expression structure
            if (node.type === 'OperatorNode') {
                // Handle linear expressions like 2*x + 3
                if (node.op === '+' || node.op === '-') {
                    const left = node.args[0];
                    const right = node.args[1];

                    // Evaluate left side if not simple
                    if (!isSimple(left)) {
                        const leftVal = evaluateNode(left);
                        steps.push({
                            description: `${nodeToString(left)} = ${formatNum(leftVal)}`,
                            value: leftVal
                        });
                    }

                    const leftResult = evaluateNode(left);
                    const rightResult = evaluateNode(right);
                    const finalResult = node.op === '+'
                        ? leftResult + rightResult
                        : leftResult - rightResult;

                    const opSymbol = node.op === '+' ? '+' : '-';
                    steps.push({
                        description: `${formatNum(leftResult)} ${opSymbol} ${formatNum(rightResult)} = ${formatNum(finalResult)}`,
                        value: finalResult
                    });
                }
                // Handle multiplication like 2*x or x^2
                else if (node.op === '*') {
                    const left = evaluateNode(node.args[0]);
                    const right = evaluateNode(node.args[1]);
                    const product = left * right;

                    steps.push({
                        description: `${formatNum(left)} × ${formatNum(right)} = ${formatNum(product)}`,
                        value: product
                    });
                }
                // Handle division
                else if (node.op === '/') {
                    const numerator = evaluateNode(node.args[0]);
                    const denominator = evaluateNode(node.args[1]);

                    if (!isSimple(node.args[0])) {
                        steps.push({
                            description: `${nodeToString(node.args[0])} = ${formatNum(numerator)}`,
                            value: numerator
                        });
                    }

                    const quotient = numerator / denominator;
                    steps.push({
                        description: `${formatNum(numerator)} ÷ ${formatNum(denominator)} = ${formatNum(quotient)}`,
                        value: quotient
                    });
                }
                // Handle exponentiation like x^2
                else if (node.op === '^') {
                    const base = evaluateNode(node.args[0]);
                    const exponent = evaluateNode(node.args[1]);
                    const power = Math.pow(base, exponent);

                    steps.push({
                        description: `${formatNum(base)}^${formatNum(exponent)} = ${formatNum(power)}`,
                        value: power
                    });
                }
            }
            // Handle function calls like sqrt(x), abs(x)
            else if (node.type === 'FunctionNode') {
                const arg = evaluateNode(node.args[0]);
                const result = evaluateNode(node);

                const funcName = node.name === 'sqrt' ? '√' : node.name;
                steps.push({
                    description: `${funcName}(${formatNum(arg)}) = ${formatNum(result)}`,
                    value: result
                });
            }

        } catch (e) {
            // Fallback: just show the substitution
            console.warn('Step calculation failed, using simple evaluation:', e);
            steps.push({
                description: `Substitute x = ${input}`,
                value: input
            });
        }

        // Always add final output
        const finalOutput = calculateOutput(input, ruleExpr);
        steps.push({
            description: `Output`,
            value: Number.isFinite(finalOutput) ? formatNum(finalOutput) : finalOutput
        });

        return steps;
    };

    const currentOutput = calculateOutput(currentInput, rule);
    const steps = config.showSteps ? calculateSteps(currentInput, rule) : [];

    useEffect(() => {
        if (!animation.enabled || !animation.autoPlay) return;

        const speedMap = { slow: 2000, normal: 1000, fast: 500 };
        const delay = speedMap[animation.speed] || 1000;

        if (animationState === 'idle') {
            setAnimationState('entering');
            const timer = setTimeout(() => setAnimationState('processing'), delay);
            return () => clearTimeout(timer);
        } else if (animationState === 'processing') {
            const timer = setTimeout(() => setAnimationState('exiting'), delay);
            return () => clearTimeout(timer);
        } else if (animationState === 'exiting') {
            setProcessedResults(prev => [
                ...prev,
                { input: currentInput, output: currentOutput }
            ]);

            if (currentInputIndex < inputValues.length - 1) {
                setCurrentInputIndex(prev => prev + 1);
                setAnimationState('idle');
            } else {
                setAnimationState('done');
            }
        }
    }, [animationState, animation, currentInputIndex, currentInput, currentOutput, inputValues.length]);

    const renderMachine = (ruleExpr, label, yOffset = 0, color = Theme.blue) => {
        const machineY = yOffset;
        return (
            <React.Fragment>
                {animationState === 'entering' && (
                    <Text x={-4} y={machineY} size={24} weight="bold">{currentInput}</Text>
                )}

                <Vector tail={[-2.8, machineY]} tip={[-1.6, machineY]} color={Theme.indigo} weight={4} />

                <Polygon
                    points={[[-1.5, machineY - 1.2], [1.5, machineY - 1.2], [1.5, machineY + 1.2], [-1.5, machineY + 1.2]]}
                    color={color}
                    fillOpacity={0.15}
                    strokeWidth={3}
                />

                {label && <Text x={0} y={machineY + 0.6} weight="bold" size={16}>{label}</Text>}
                <Text x={0} y={machineY - 0.3} size={20} color={color}>{ruleExpr}</Text>

                {animationState === 'processing' && config.showSteps && (
                    <React.Fragment>
                        {steps.map((step, i) => (
                            <Text key={i} x={0} y={machineY - 0.6 - (i * 0.3)} size={12} color={color}>
                                {step.description}
                            </Text>
                        ))}
                    </React.Fragment>
                )}

                <Vector tail={[1.6, machineY]} tip={[2.8, machineY]} color={Theme.indigo} weight={4} />

                {(animationState === 'exiting' || animationState === 'done') && (
                    <Text x={4} y={machineY} size={24} weight="bold" color={Theme.green}>
                        {formatNum(calculateOutput(currentInput, ruleExpr))}
                    </Text>
                )}
            </React.Fragment>
        );
    };

    const renderTable = () => {
        if (!config.showTable || processedResults.length === 0) return null;
        return (
            <div className="ch-function-machine-table" style={{ marginTop: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
                    <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '8px' }}>Input (x)</th>
                        <th style={{ padding: '8px' }}>Output f(x)</th>
                        {studentRule && <th style={{ padding: '8px' }}>Student g(x)</th>}
                    </tr>
                    </thead>
                    <tbody>
                    {processedResults.map((result, i) => {
                        const studentOut = studentRule ? calculateOutput(result.input, studentRule) : null;
                        const match = studentOut === result.output;
                        return (
                            <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '8px' }}>{result.input}</td>
                                <td style={{ padding: '8px', color: 'green' }}>{formatNum(result.output)} ✓</td>
                                {studentRule && (
                                    <td style={{ padding: '8px', color: match ? 'green' : 'red' }}>
                                        {formatNum(studentOut)} {match ? "✓" : "✗"}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderGraph = () => {
        if (!config.showGraph) return null;
        const points = processedResults.map(r => [r.input, r.output]);
        if (points.length === 0) return null;

        const xMin = Math.min(...points.map(p => p[0])) - 1;
        const xMax = Math.max(...points.map(p => p[0])) + 1;
        const yMin = Math.min(...points.map(p => p[1])) - 1;
        const yMax = Math.max(...points.map(p => p[1])) + 1;

        return (
            <div style={{ marginTop: '20px' }}>
                <Mafs viewBox={{ x: [xMin, xMax], y: [yMin, yMax] }} height={200}>
                    <Coordinates.Cartesian />
                    {points.map((point, i) => (
                        <Point key={i} x={point[0]} y={point[1]} color={Theme.blue} />
                    ))}
                    {points.length >= 2 && (
                        <Plot.OfX y={(x) => calculateOutput(x, rule)} color={Theme.blue} style="dashed" />
                    )}
                </Mafs>
            </div>
        );
    };

    return (
        <div className="ch-function-machine">
            <div className="ch-machine-header" style={{ marginBottom: '15px' }}>
                <h3>Function: f(x) = {rule}</h3>
                {config.verbalDescription && <p className="ch-verbal-description" style={{ fontStyle: 'italic' }}>{config.verbalDescription}</p>}
            </div>

            <Mafs viewBox={{ x: [-5, 5], y: [-3, 3] }} height={300} pan={false}>
                {renderMachine(rule, "f(x)", 0, Theme.blue)}
                {showInverse && inverseRule && renderMachine(inverseRule, "f⁻¹(x)", -2.5, Theme.orange)}
                {studentRule && renderMachine(studentRule, "g(x) [Student]", 2.5, "#fa8c16")}
            </Mafs>

            {renderTable()}
            {renderGraph()}

            {animation.enabled && !animation.autoPlay && (
                <div className="ch-machine-controls" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                    <button
                        disabled={animationState !== 'idle' && animationState !== 'done'}
                        onClick={() => setAnimationState('idle')}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                    >
                        Process Next Input
                    </button>
                    <button
                        onClick={() => {
                            setCurrentInputIndex(0);
                            setProcessedResults([]);
                            setAnimationState('idle');
                        }}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                    >
                        Reset
                    </button>
                </div>
            )}
        </div>
    );
};